import { Container, Divider } from "@material-ui/core";
import { Grid } from "@mui/material";
import axios from "axios";
import {
  AutoCompleteComponent,
  ButtonGroupTop,
  CustomTable,
  DateRangePicker,
  DateTimeDisplay,
  ModalError,
  ModalSuccess,
  Page,
  SearchTextInput,
  SelectInput,
  StatusChip,
} from "components";
import { HeaderTitle } from "layouts";
import { getTotalPage } from "lib";
import { debounce } from "lodash";
import moment from "moment-timezone";
import { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router";
import { hardBaseUrl } from "services/urlConstant";
import { globalStyles } from "styles";
import { formatPayloadDate, getErrors } from "utils";
import { breadcrumbData } from "../Components/SongClaimMenu";

function UnclaimMonitoring({ userLogin }) {
  const classes = globalStyles();
  const history = useHistory();
  const token = localStorage.getItem("token");
  const headers = {
    Authorization: `Bearer ${token}`,
  };
  const isAdmin = userLogin?.role?.type === "admin";

  const [loadingPage, setLoadingPage] = useState(true);
  const [queryParams, setQueryParams] = useState({
    page: 1,
    size: 10,
    type: "UM",
    ...(isAdmin && {
      start_date: moment().subtract(1, "months"),
      end_date: moment(),
      publisher_id: 0,
      status: "",
    }),
  });
  const [tableData, setTableData] = useState([]);
  const [tablePageCount, setTablePageCount] = useState(1);
  const [optionPublisher, setOptionPublisher] = useState([]);
  const [loadingFilter, setLoadingFilter] = useState(false);

  const handleChangeQueryParams = (value, key) => {
    setQueryParams(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
  };
  const handleChangeSearch = event => {
    const { value } = event?.target;
    handleChangeQueryParams(value, "search");
  };
  const getDataTable = async () => {
    setLoadingPage(true);
    const { start_date, end_date, ...restQuery } = queryParams;
    const params = {
      ...(isAdmin && {
        start_date: formatPayloadDate(start_date),
        end_date: formatPayloadDate(end_date),
      }),
      ...restQuery,
    };
    try {
      const res = await axios.get(`${hardBaseUrl}/process/datatable`, {
        headers,
        params,
      });
      const resData = res?.data?.data;
      const resTotalSize = res?.data?.meta?.total;
      setTableData(resData);
      setTablePageCount(getTotalPage(resTotalSize, queryParams?.size));
    } catch (error) {
      ModalError(getErrors(error));
    } finally {
      setLoadingPage(false);
    }
  };
  const debounceDataTable = useCallback(
    debounce(() => getDataTable(), 500),
    [queryParams]
  );
  const getOptionsPublisher = async () => {
    setLoadingFilter(true);
    try {
      const res = await axios.get(`${hardBaseUrl}/process/publisher`, {
        headers,
      });
      const modifiedData = res?.data?.data?.map(item => ({
        ...item,
        id: item?.publisher_id,
        label: item?.name,
      }));
      setOptionPublisher(modifiedData);
    } catch (error) {
      ModalError(getErrors(error));
    } finally {
      setLoadingFilter(false);
    }
  };
  const handleRetryUnclaimProcess = async id => {
    setLoadingPage(true);
    try {
      await axios.post(`${hardBaseUrl}/unclaim/retry/${id}`, {}, { headers });
      ModalSuccess("Successfully Retry Unclaim Process").then(() =>
        getDataTable()
      );
    } catch (error) {
      ModalError(getErrors(error));
    } finally {
      setLoadingPage(false);
    }
  };

  useEffect(() => {
    getOptionsPublisher();
  }, []);
  useEffect(() => {
    const isSearching = Boolean(queryParams?.search);
    if (isSearching) {
      debounceDataTable();
      return () => {
        debounceDataTable.cancel();
      };
    } else {
      getDataTable();
    }
  }, [queryParams, debounceDataTable]);

  return (
    <Page className={classes?.root} title="Unclaim Monitoring">
      <Container maxWidth={false}>
        <HeaderTitle title="Song Claim" breadcrumbData={breadcrumbData} />
        <ButtonGroupTop />
        <Divider className={classes?.flatDivider} />
        {isAdmin && (
          <Grid container justifyContent="space-between" my={2}>
            <Grid item>
              <SearchTextInput
                placeholder="Search"
                value={queryParams?.search}
                onChange={handleChangeSearch}
                disabled={loadingPage}
              />
            </Grid>
            <Grid item>
              <Grid container columnSpacing={1}>
                <Grid item>
                  <DateRangePicker
                    label="Period"
                    startDate={queryParams?.start_date}
                    handleChangeStartDate={date =>
                      handleChangeQueryParams(date, "start_date")
                    }
                    endDate={queryParams?.end_date}
                    handleChangeEndDate={date =>
                      handleChangeQueryParams(date, "end_date")
                    }
                  />
                </Grid>
                <Grid item>
                  <AutoCompleteComponent
                    label="Publisher"
                    options={optionPublisher}
                    value={
                      optionPublisher?.find(
                        option => option?.id === queryParams?.publisher_id
                      ) || null
                    }
                    onChange={val =>
                      handleChangeQueryParams(val, "publisher_id")
                    }
                    size="small"
                    disabled={loadingPage || loadingFilter}
                  />
                </Grid>
                <Grid item>
                  <SelectInput
                    label="Status"
                    value={queryParams?.status}
                    onChange={e => {
                      handleChangeQueryParams(e?.target?.value, "status");
                    }}
                    options={optionStatus}
                    optionKey="value"
                    optionLabel="label"
                    placeholder="All"
                    width={250}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}
        <CustomTable
          isLoading={loadingPage}
          columnTable={columnTable({ isAdmin })}
          data={tableData || []}
          page={queryParams?.page}
          pageSize={queryParams?.size}
          pageCount={tablePageCount}
          handleChangePage={(_, page) => handleChangeQueryParams(page, "page")}
          handleChangePageSize={e =>
            handleChangeQueryParams(e?.target?.value, "size")
          }
          isHaveAction
          handleView={item =>
            history.push(`/admin/publisher/unclaim-monitoring/${item?.id}`)
          }
          tooltipView="View Detail"
          disabledRestore={item => item.status !== "Error"}
          handleRestore={item => handleRetryUnclaimProcess(item?.id)}
          tooltipRestore="Retry Process"
          mt={2}
        />
      </Container>
    </Page>
  );
}
const optionStatus = [
  { value: "finish", label: "Finish" },
  { value: "failed", label: "Request Time Out" },
  { value: "queued", label: "Running" },
];
const columnTable = ({ isAdmin }) => [
  {
    name: "id",
    title: "Unclaim ID",
  },
  {
    name: "start_date_time",
    title: "Start Date",
    renderText: item => <DateTimeDisplay date={item} />,
  },
  {
    name: "end_date_time",
    title: "End Date",
    renderText: item => {
      if (!item) {
        return "-";
      }

      const dateObject = new Date(item);
      const isInvalidDate =
        isNaN(dateObject) || dateObject.getFullYear() < 2000;

      if (isInvalidDate) {
        return "-";
      }

      return <DateTimeDisplay date={item} />;
    },
  },
  ...(isAdmin
    ? [
        {
          name: "publisher",
          title: "Publisher",
          renderText: item => item?.name || "-",
        },
      ]
    : []),
  {
    name: "status",
    title: "Status",
    renderText: status => (
      <StatusChip
        type={
          status === "Finish"
            ? "success"
            : status === "Error"
            ? "danger"
            : status === "Running"
            ? "warning"
            : ""
        }
        label={status === "Error" ? "Request Time Out" : status}
      />
    ),
  },
];
export default UnclaimMonitoring;
